import { string, object, type InferType } from 'yup';
import type { Nullable } from '@/types';
import { inject, ref, type Ref } from 'vue';
import type { WellKnownPlace, Landmark, Address } from '@/types/customer/address.types';
import { type AddressCategory } from '@/components/customerPage/addressSearch/addressSearch.types';

export type AutofocusCategories = Extract<AddressCategory, 'favorite' | 'recent' | 'search'>;
export type AutofocusElement = 'input' | 'label';
export type AutofocusCategoryElementMap = Record<AutofocusCategories, AutofocusElement>;

export type { LandmarkType } from '@/types/customer/address.types';

export type AddressFormItemContext = {
  selectedDate: Ref<string>;
  homeAddress: Ref<Nullable<Address>>;
  restrictedAddresses: Ref<boolean>;
};

export const ADDRESS_FORM_ITEM_CONTEXT_KEY = Symbol();

export const getEmptyAddressFormSelection = (): IAddressFormSelection => ({
  address: {
    key: '',
    isHome: false,
    isPrivate: true,
    place: null,
    address: '',
    civicNumber: '',
    streetDescription: '',
    postalCode: '',
    suite: null,
    municipality: '',
    instruction: null,
    coordinateWgs84: null,
  },
  category: null,
  apartment: '',
  driverNote: null,
});

export function injectAddressFormItemContext(): AddressFormItemContext {
  return inject(ADDRESS_FORM_ITEM_CONTEXT_KEY, {
    selectedDate: ref(''),
    homeAddress: ref(null),
    restrictedAddresses: ref(false),
  });
}

/** Combinaison de l'adresse et la catégorie associée à l'adresse sélectionnée. */
export type IAddressFormSelection = {
  address: Address;
  category: Nullable<AddressCategory>;
  apartment: string | null;
  /** La note ajoutée par l'utilisateur pour l'adresse sélectionnée dans le nouveau déplacement en cours de création */
  driverNote: string | null;
};

export const stepAddressFormSchema = object({
  originAddress: getAddressItemSchema('pages.tripBooking.forms.items.address.originAddress.validations.required.inlineLabel'),
  destinationAddress: getAddressItemSchema('pages.tripBooking.forms.items.address.destinationAddress.validations.required.inlineLabel'),
  dialysis: string().nullable().default(null).oneOf(['true', 'false']),
});

export function getAddressItemSchema(requiredI18nKey: string) {
  return object({
    address: string().default('').required(requiredI18nKey),
    apartment: string().optional().nullable().default(''),
    driverNote: string().optional().nullable().default(null),
    originalInstruction: string().optional().nullable().default(null),
  });
}

export type AddressItemSchema = InferType<ReturnType<typeof getAddressItemSchema>>;

export type AddressSchema = InferType<typeof stepAddressFormSchema>;

export type {
  WellKnownPlace,
  Landmark,
};

/** Nom de l'élément de formulaire pour l'adresse d'origine */
export const FORM_ITEM_NAME_ORIGIN = 'originAddress';
/** Nom de l'élément de formulaire pour l'adresse de destination */
export const FORM_ITEM_NAME_DESTINATION = 'destinationAddress';
/** Nom de l'élément de formulaire pour la sélection de dialyse */
export const FORM_ITEM_DIALYSIS = 'dialysis';

/** Mapping de valeurs entre le nom de l'élément de formulaire et le type d'adresse (origine ou destination) */
export const formItemNameToAdressType: Record<'originAddress' | 'destinationAddress', AddressType> = {
  [FORM_ITEM_NAME_ORIGIN]: 'origin',
  [FORM_ITEM_NAME_DESTINATION]: 'destination',
};

/** Type d'adresse défini pour l'élément de formulaire donné */
export type AddressType = 'origin' | 'destination';
